import { readOfflineQueue } from '@/offline/offlineQueue';

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;
const APP_UPDATE_READY_EVENT = 'nangbook-app-update-ready';
const FORM_CONTROL_SELECTOR = 'input, textarea, select, [contenteditable="true"]';

let appUpdateReady = false;
let reloadingForUpdate = false;
let updateCheckIntervalId: number | null = null;

export function registerOfflineServiceWorker(): void {
  if (!('serviceWorker' in navigator) || import.meta.env.DEV) {
    return;
  }

  window.addEventListener('load', () => {
    const hadControllerAtStartup = Boolean(navigator.serviceWorker.controller);
    let hasControlledThisPage = hadControllerAtStartup;

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hasControlledThisPage) {
        hasControlledThisPage = true;
        return;
      }

      markAppUpdateReady();
    });

    navigator.serviceWorker
      .register('/offline-sw.js', { updateViaCache: 'none' })
      .then((registration) => {
        wireRegistration(registration);
        scheduleUpdateChecks(registration);
        void registration.update();
      })
      .catch(() => {
        // Offline support is an enhancement; the app should still boot if
        // the browser refuses service worker registration.
      });
  });
}

export function isAppUpdateReady(): boolean {
  return appUpdateReady;
}

export function attemptSilentAppUpdateReload(reason: 'route-transition' | 'page-hidden' | 'update-ready'): boolean {
  if (!appUpdateReady || reloadingForUpdate || !canReloadForUpdate(reason)) {
    return false;
  }

  reloadingForUpdate = true;
  window.location.reload();

  return true;
}

export function subscribeToAppUpdateReady(callback: () => void): () => void {
  window.addEventListener(APP_UPDATE_READY_EVENT, callback);

  return () => window.removeEventListener(APP_UPDATE_READY_EVENT, callback);
}

function wireRegistration(registration: ServiceWorkerRegistration): void {
  registration.addEventListener('updatefound', () => {
    const installingWorker = registration.installing;

    if (!installingWorker) return;

    installingWorker.addEventListener('statechange', () => {
      if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
        registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
      }
    });
  });

  if (registration.waiting && navigator.serviceWorker.controller) {
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  }
}

function scheduleUpdateChecks(registration: ServiceWorkerRegistration): void {
  if (updateCheckIntervalId !== null) {
    window.clearInterval(updateCheckIntervalId);
  }

  updateCheckIntervalId = window.setInterval(() => {
    void registration.update();
  }, UPDATE_CHECK_INTERVAL_MS);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void registration.update();
      return;
    }

    attemptSilentAppUpdateReload('page-hidden');
  });
}

function markAppUpdateReady(): void {
  appUpdateReady = true;
  window.dispatchEvent(new Event(APP_UPDATE_READY_EVENT));
  attemptSilentAppUpdateReload(document.visibilityState === 'hidden' ? 'page-hidden' : 'update-ready');
}

function canReloadForUpdate(reason: 'route-transition' | 'page-hidden' | 'update-ready'): boolean {
  if (!navigator.onLine || readOfflineQueue().length > 0) {
    return false;
  }

  if (reason === 'page-hidden') {
    return true;
  }

  if (reason === 'route-transition') {
    return !hasFocusedFormControl();
  }

  return false;
}

function hasFocusedFormControl(): boolean {
  const activeElement = document.activeElement;

  return activeElement instanceof Element && activeElement.matches(FORM_CONTROL_SELECTOR);
}
